import { z } from 'zod';
// Project catalogs, managed in Master Data under "הגדרות מסירות": apartment areas, inspection
// components, work categories, the contractors who perform the work (H-2) and, apart from them,
// the suppliers of materials (DR-188). One shared model; entries are deactivated, never deleted.
// Business contacts are synthetic in development.
export const CATALOG_KINDS = [
    'area',
    'component',
    'contractor',
    'work_category',
    'supplier',
];
export const CATALOG_KIND_LABELS = {
    contractor: 'קבלנים מבצעים',
    work_category: 'תחומי עבודה',
    area: 'חדרים',
    component: 'רכיבים לבדיקה',
    supplier: 'ספקים',
};
/** What a supplier supplies (DR-188): a supplier never performs work or receives findings. */
export const SUPPLY_TYPES = ['kitchen', 'stone', 'tiles'];
export const SUPPLY_TYPE_LABELS = {
    kitchen: 'מטבחים',
    stone: 'שיש',
    tiles: 'ריצוף',
};
/** Kinds that hold a business contact (name, phone, email). */
export const CONTACT_KINDS = ['contractor', 'supplier'];
export const CATALOG_LIMITS = {
    name: 80,
    contactName: 80,
    contactPhone: 25,
    contactEmail: 254,
    entries: 500,
};
export const PHONE_PATTERN = /^[0-9+][0-9 ()+-]{5,24}$/;
export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+$/;
const optionalText = (max, pattern) => {
    const base = z.string().trim().max(max);
    return (pattern === undefined ? base : base.regex(pattern).or(z.literal('')))
        .nullable()
        .transform((value) => (value === '' ? null : value));
};
/** A contractor's business contact, sent only to callers allowed to see it. */
export const catalogContactSchema = z
    .object({
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    contactEmail: z.string().nullable(),
})
    .strict();
export const catalogEntrySchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    active: z.boolean(),
    rowVersion: z.number().int().min(1),
    /** Contractors and suppliers only, and only with `projects.supplier.view`; otherwise null. */
    contact: catalogContactSchema.nullable(),
    /** Suppliers only: what they supply; otherwise null. */
    supplyType: z.enum(SUPPLY_TYPES).nullable(),
    /** Contractors only: the work categories (תחומי עבודה) they belong to; otherwise empty. */
    categoryIds: z.array(z.uuid()),
    /** Work categories only: the contractor suggested for its new findings (H-Checklist). */
    defaultContractorId: z.uuid().nullable(),
})
    .strict();
/**
 * `GET` and `PUT /projects/:id/catalogs/:kind`: every entry of the kind in display order, and the
 * version of the whole list (it changes whenever any entry of the kind changes).
 */
export const catalogListResponseSchema = z
    .object({
    kind: z.enum(CATALOG_KINDS),
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    items: z.array(catalogEntrySchema),
    permissions: z.object({ edit: z.boolean(), contacts: z.boolean() }).strict(),
})
    .strict();
export const catalogRowInputSchema = z
    .object({
    /** null: a new entry. */
    id: z.uuid().nullable(),
    name: z.string().trim().min(1).max(CATALOG_LIMITS.name),
    active: z.boolean(),
    /** Contractors and suppliers only: the server refuses contact values for other kinds. */
    contactName: optionalText(CATALOG_LIMITS.contactName).optional(),
    contactPhone: optionalText(CATALOG_LIMITS.contactPhone, PHONE_PATTERN).optional(),
    contactEmail: optionalText(CATALOG_LIMITS.contactEmail, EMAIL_PATTERN).optional(),
    /** Suppliers only, and required for a new one: what they supply. Absent: unchanged. */
    supplyType: z.enum(SUPPLY_TYPES).optional(),
    /** Contractors only: their work categories. Absent: unchanged. */
    categoryIds: z.array(z.uuid()).max(30).optional(),
    /**
     * Work categories only: the default contractor, one of the category's contractors (null: none).
     * Absent: unchanged.
     */
    defaultContractorId: z.uuid().nullable().optional(),
})
    .strict();
/**
 * `PUT /projects/:id/catalogs/:kind`: the whole list as the user sees it, in display order. Every
 * stored entry must be listed (entries are deactivated, never removed); `listVersion` is the
 * version of the list that was edited, so a list changed meanwhile is refused (409).
 */
export const catalogSaveRequestSchema = z
    .object({
    listVersion: z.string().regex(/^[0-9a-f]{64}$/),
    items: z.array(catalogRowInputSchema).max(CATALOG_LIMITS.entries),
})
    .strict()
    .superRefine((request, context) => {
    const ids = new Set();
    const names = new Set();
    request.items.forEach((item, index) => {
        if (item.id !== null) {
            if (ids.has(item.id))
                context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'id'] });
            ids.add(item.id);
        }
        const name = item.name.toLowerCase();
        if (names.has(name))
            context.addIssue({ code: 'custom', message: 'duplicate', path: ['items', index, 'name'] });
        names.add(name);
    });
});
//# sourceMappingURL=catalog.js.map